import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { socketService } from '../../services/SocketService';
import { showCustomToast } from '../common/CustomToast';
import ChatSkeleton from './ChatSkeleton';
import { getProfilePictureUrl } from '../../utils/profilePictureUtils';
import phantomAvatar from '../../assets/phantom.png';
import './ChatSidebar.css';

interface ChatMessage {
  id: string;
  message: string;
  createdAt: string;
  user: {
    id: string;
    username: string;
    isAdmin: boolean;
    profilePicture: string | null;
  };
  isNew?: boolean; // Flag for animation
}

interface ChatSidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

const MESSAGES_TO_RENDER = 50; // Limit messages displayed for performance
const MESSAGES_TO_KEEP = 100; // Keep more in memory but don't render all

const ChatSidebar: React.FC<ChatSidebarProps> = ({ isOpen, onToggle }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [hoveredMessage, setHoveredMessage] = useState<string | null>(null);
  const [onlineCount, setOnlineCount] = useState(0);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const cooldownTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  const { user, token } = useSelector((state: RootState) => state.auth);

  // Only render the most recent messages for performance
  const visibleMessages = useMemo(() => {
    return messages.slice(-MESSAGES_TO_RENDER);
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadMessages = React.useCallback(async () => {
    const startTime = Date.now();
    setIsLoading(true);
    
    try {
      const headers: HeadersInit = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/chat/messages?limit=50`, {
        headers
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages.map((msg: ChatMessage) => ({ ...msg, isNew: false })));
        setIsInitialLoad(false);
      } else if (response.status === 429) {
        console.warn('Rate limited - skipping message load');
      } else if (response.status === 401) {
        console.warn('Unauthorized - viewing as guest');
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      // Ensure skeleton shows for at least 2 seconds
      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(0, 2000 - elapsed);
      
      setTimeout(() => {
        setIsLoading(false);
      }, remainingTime);
    }
  }, [token]);

  const joinChatRoom = React.useCallback(() => {
    if (socketService.isConnected()) {
      socketService.joinChatRoom();
    }
  }, []);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    if (isOpen) {
      // Debounce to prevent rapid API calls
      timeoutId = setTimeout(() => {
        loadMessages();
        if (token) {
          joinChatRoom();
        }
      }, 500);
    }
    
    return () => {
      clearTimeout(timeoutId);
      if (socketService.isConnected()) {
        socketService.leaveChatRoom();
      }
    };
  }, [isOpen, token, loadMessages, joinChatRoom]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Socket subscriptions useEffect - handles all socket event subscriptions
  useEffect(() => {
    if (!socketService.isConnected()) {
      return;
    }

    // Subscribe to messages
    const unsubscribeMessages = socketService.subscribeToChatMessages((message: ChatMessage) => {
      setMessages(prev => {
        if (prev.some(m => m.id === message.id)) {
          return prev;
        }
        // Mark new message with 'new' flag for animation
        const newMsg = { ...message, isNew: true };
        const updated = [...prev, newMsg];
        
        // Keep only the most recent MESSAGES_TO_KEEP messages in memory
        if (updated.length > MESSAGES_TO_KEEP) {
          return updated.slice(-MESSAGES_TO_KEEP);
        }
        return updated;
      });
      
      // Remove animation flag after animation completes
      setTimeout(() => {
        setMessages(prev => prev.map(m => 
          m.id === message.id ? { ...m, isNew: false } : m
        ));
      }, 300);
    });

    // Subscribe to message deletions
    const unsubscribeDeletions = socketService.subscribeToChatMessageDeletions((data: { messageId: string }) => {
      setMessages(prev => prev.filter(msg => msg.id !== data.messageId));
    });

    // Subscribe to online count updates
    const unsubscribeOnlineCount = socketService.subscribeToOnlineCount((count: number) => {
      setOnlineCount(count);
    });

    // Return cleanup function to unsubscribe
    return () => {
      unsubscribeMessages();
      unsubscribeDeletions();
      unsubscribeOnlineCount();
    };
  }, []);

  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newMessage.trim() || !token || isSending || cooldownRemaining > 0) return;

    const messageText = newMessage.trim();
    
    // Check word count (max 12 words)
    const wordCount = messageText.split(/\s+/).filter(word => word.length > 0).length;
    if (wordCount > 12) {
      showCustomToast.error('Message cannot exceed 12 words');
      return;
    }
    
    setNewMessage('');
    setIsSending(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/chat/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ message: messageText })
      });

      if (!response.ok) {
        const error = await response.json();
        
        // Handle cooldown error
        if (response.status === 429 && error.remainingSeconds) {
          setCooldownRemaining(error.remainingSeconds);
          startCooldownTimer(error.remainingSeconds);
          showCustomToast.error(error.error || 'Please wait before sending another message');
        } else {
          showCustomToast.error(error.error || 'Failed to send message');
        }
        setNewMessage(messageText);
      } else {
        // Start cooldown after successful send (admins exempt on backend)
        if (!user?.isAdmin) {
          setCooldownRemaining(5);
          startCooldownTimer(5);
        }
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      showCustomToast.error('Failed to send message');
      setNewMessage(messageText);
    } finally {
      setIsSending(false);
    }
  };

  const startCooldownTimer = (seconds: number) => {
    // Clear any existing timer
    if (cooldownTimerRef.current) {
      clearInterval(cooldownTimerRef.current);
    }

    let remaining = seconds;
    setCooldownRemaining(remaining);

    cooldownTimerRef.current = setInterval(() => {
      remaining -= 1;
      setCooldownRemaining(remaining);

      if (remaining <= 0) {
        if (cooldownTimerRef.current) {
          clearInterval(cooldownTimerRef.current);
          cooldownTimerRef.current = null;
        }
      }
    }, 1000);
  };

  // Cleanup cooldown timer on unmount
  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
      }
    };
  }, []);

  const muteUser = async (userId: string, username: string) => {
    if (!token || !user?.isAdmin) return;
    
    const duration = prompt(`Mute ${username} for how many minutes?`, '60');
    if (!duration || isNaN(Number(duration))) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/admin/users/${userId}/mute`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ duration: parseInt(duration) })
      });
      
      if (response.ok) {
        showCustomToast.success(`${username} muted for ${duration} minutes`);
      } else {
        const error = await response.json();
        showCustomToast.error(error.error || 'Failed to mute user');
      }
    } catch (error) {
      console.error('Failed to mute user:', error);
      showCustomToast.error('Failed to mute user');
    }
  };

  const deleteMessage = async (messageId: string) => {
    if (!token || !user?.isAdmin) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/chat/messages/${messageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        showCustomToast.success('Message deleted');
      } else {
        const error = await response.json();
        showCustomToast.error(error.error || 'Failed to delete message');
      }
    } catch (error) {
      console.error('Failed to delete message:', error);
      showCustomToast.error('Failed to delete message');
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getProfilePicture = (profilePicture: string | null) => {
    const url = getProfilePictureUrl(profilePicture);
    return url || phantomAvatar; // Use phantom.png as default
  };

  return (
    <div className={`chat-sidebar ${isOpen ? 'open' : 'closed'}`}>
      {isInitialLoad && isLoading ? (
        <ChatSkeleton />
      ) : (
        <>
          {/* Header */}
          <div className="chat-sidebar-header">
            <h3>Live Chat</h3>
            <div className="online-count">
              <span className="pulse-dot"></span>
              <span className="count">{onlineCount} Online</span>
            </div>
          </div>
          
          {/* Messages */}
          <div className="chat-sidebar-messages">
            {visibleMessages.map((message: ChatMessage, index: number) => (
              <div 
                key={message.id} 
                className={`chat-sidebar-message ${message.isNew ? 'message-new' : 'message-initial'}`}
                style={{ animationDelay: !message.isNew && isInitialLoad ? `${index * 0.03}s` : '0s' }}
                onMouseEnter={() => setHoveredMessage(message.id)}
                onMouseLeave={() => setHoveredMessage(null)}
              >
                <img 
                  src={getProfilePicture(message.user.profilePicture)}
                  alt={message.user.username}
                  className="chat-sidebar-avatar"
                  onError={(e) => {
                    e.currentTarget.src = phantomAvatar;
                  }}
                />
                <div className="chat-sidebar-message-content">
                  <div className="chat-sidebar-message-header">
                    <span className={`chat-sidebar-username ${message.user.isAdmin ? 'admin' : ''}`}>
                      {message.user.username}
                      {message.user.isAdmin && <span className="chat-sidebar-admin-badge">ADMIN</span>}
                    </span>
                    <span className="chat-sidebar-time">{formatTime(message.createdAt)}</span>
                  </div>
                  <div className="chat-sidebar-message-text">{message.message}</div>
                </div>
                {user?.isAdmin && hoveredMessage === message.id && !message.user.isAdmin && (
                  <div className="admin-actions">
                    <button 
                      className="admin-action-btn mute"
                      onClick={() => muteUser(message.user.id, message.user.username)}
                      title="Mute user"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <polyline points="12 6 12 12 16 14"/>
                      </svg>
                    </button>
                    <button 
                      className="admin-action-btn delete"
                      onClick={() => deleteMessage(message.id)}
                      title="Delete message"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6"/>
                        <path d="m19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <form className="chat-sidebar-input-form" onSubmit={sendMessage}>
            <input
              ref={inputRef}
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={!user ? "Login to chat..." : cooldownRemaining > 0 ? `Wait ${cooldownRemaining}s...` : "Type a message..."}
              maxLength={500}
              disabled={!user || isSending || cooldownRemaining > 0}
              className="chat-sidebar-input"
            />
            <button 
              type="submit" 
              disabled={!user || !newMessage.trim() || isSending || cooldownRemaining > 0}
              className="chat-sidebar-send-btn"
            >
              {!user ? 'LOGIN' : cooldownRemaining > 0 ? `${cooldownRemaining}s` : isSending ? '...' : 'SEND'}
            </button>
          </form>
        </>
      )}
    </div>
  );
};

export default ChatSidebar;
